import {
  Button,
  Callout,
  Card,
  FormLayout,
  GridFieldGroup,
  Heading,
  LoadingDots,
  Paragraph,
  SubmitButton,
  TextInput,
} from '@expressable/syntax-ds';

import { zodResolver } from '@hookform/resolvers/zod';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { ResumeSignupLeadFormType, resumeSignupLeadSchema } from 'domains/signupDirect/ResumeLead/resumeSchema';
import { onSubmitLeadVerification } from 'domains/signupDirect/ResumeLead/submitVerificationStep';
import { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from 'router/routes';
import { getSignupAppointment } from 'domains/signupDirect/ResumeLead/PrefilledAppointment/appointment';
import { parseIntSafe } from 'utils/currency';
import { useSalesforceLead } from 'domains/signupv4/Leads/hooks';
import { SealQuestion } from '@phosphor-icons/react';
import config from 'config';

// Helper function to get the appropriate label key based on isAdult value
const getLabelKey = (baseKey: string, isAdult: boolean | null | undefined): string => {
  if (isAdult === null || isAdult === undefined) {
    return baseKey; // Return base key (e.g., 'directSignup.labels.clientFirstName')
  }
  if (isAdult === true) {
    return `${baseKey}.adult`; // Return adult key (e.g., 'directSignup.labels.clientFirstName.adult')
  }
  return `${baseKey}.child`; // Return child key (e.g., 'directSignup.labels.clientFirstName.child')
};

const DirectSignupResumeLeadStep = () => {
  const { onResumeFromLeadData } = useSignupDirectCtx();
  const [validationFailed, setValidationFailed] = useState(false);
  const { t } = useTranslation();

  const [params] = useSearchParams();
  const navigate = useNavigate();

  const leadID = params.get('leadID');
  const rid = params.get('rid');

  const { isLoading, data: salesforceLeadData, isError } = useSalesforceLead(leadID || '');

  if (!leadID) {
    return <Navigate to={routes.signupDirect.base} />;
  }

  const methods = useForm<ResumeSignupLeadFormType>({
    resolver: zodResolver(resumeSignupLeadSchema),
    defaultValues: {
      leadID,
    },
  });

  const { handleSubmit } = methods;
  const adult = isLoading || isError ? null : salesforceLeadData?.isAdult ?? false;
  const supportEmail = config.get('supportEmail');
  return (
    <>
      {isLoading && (
        <div className="flex justify-center items-center min-h-screen mx-auto">
          <LoadingDots />
        </div>
      )}

      {!isLoading && (
        <>
          <div>
            <Heading level="h1">{t(getLabelKey('directSignup.resumeLead.form.title', adult))}</Heading>
            <Paragraph size="lg" noDefaultMargins>
              {t('directSignup.resumeLead.form.subTitle')}
            </Paragraph>
          </div>

          <FormProvider {...methods}>
            <FormLayout
              className="mobile-xs-d:gap-e40 tablet-d:gap-e56"
              onSubmit={handleSubmit(async d => {
                const data = await onSubmitLeadVerification(d);
                const reservationId = parseIntSafe(rid);

                const appointment = await (reservationId ? getSignupAppointment(reservationId) : undefined);
                if (!data) {
                  setValidationFailed(true);
                } else if (data.expired) {
                  onResumeFromLeadData(data);
                } else {
                  onResumeFromLeadData({
                    ...data,
                    resumedLeadState: {
                      ...data.resumedLeadState,
                      prefilledAppointment: appointment,
                    },
                  });
                }
              })}
            >
              <FormFields />
              <FormLayout.CTA>
                <SubmitButton data-testid="resume-lead-submit-button">
                  {t('directSignup.resumeLead.form.submit')}
                </SubmitButton>
                {validationFailed && (
                  <Button onClick={() => navigate(routes.signupDirect.base)} disabled={isLoading} variant="link">
                    {t('directSignup.resumeLead.form.startOver')}
                  </Button>
                )}
              </FormLayout.CTA>
              {validationFailed && <Callout type="warning">{t('directSignup.resumeLead.form.failedCallout')}</Callout>}

              <Card noDefaultPaddings>
                <div className="flex flex-col divide-y-2">
                  <Heading level="h5" className="gap-e16 flex items-center p-e32" noDefaultMargins>
                    <div className="p-e10 rounded-full bg-orchid-50 text-purple-500">
                      <SealQuestion weight="fill" size={20} />
                    </div>
                    {t(getLabelKey('directSignup.resumeLead.form.card.title', adult))}
                  </Heading>
                  <div className="p-e32 flex flex-col gap-e32">
                    {t(getLabelKey('directSignup.resumeLead.form.card.description', adult))}
                  </div>
                </div>

                <FormLayout.CTA className="p-e32 pt-0 mobile-d:flex-col empty:hidden">
                  <Button
                    onClick={() => window.open(`mailto:${supportEmail}`, '_blank')}
                    disabled={isLoading}
                    variant="secondary"
                  >
                    {t('accountMenu.contactSupportButtonText')}
                  </Button>
                </FormLayout.CTA>
              </Card>
            </FormLayout>
          </FormProvider>
        </>
      )}
    </>
  );
};

const FormFields = () => {
  const { t } = useTranslation();
  const { register } = useFormContext<ResumeSignupLeadFormType>();

  return (
    <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
      <GridFieldGroup>
        <TextInput
          data-testid="client-email-input"
          label={t('directSignup.resumeLead.form.fields.email')}
          type="email"
          {...register('email')}
        />
        <TextInput
          type="tel"
          data-testid="client-phone-input"
          label={t('directSignup.resumeLead.form.fields.phone')}
          {...register('phone')}
        />
      </GridFieldGroup>
    </FormLayout.Fields>
  );
};

export default DirectSignupResumeLeadStep;
